import React, { useState, useEffect, useRef, createContext, useContext } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useNavigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import toast from 'react-hot-toast'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { usePlano } from './hooks/usePlano'
import { supabase } from './lib/supabase'
import AuthPage from './pages/AuthPage'
import GruposPage from './pages/GruposPage'
import FeedPage from './pages/FeedPage'
import SetlistsPage from './pages/SetlistsPage'
import MembrosPage from './pages/MembrosPage'
import ShowAoVivoModal from './components/ShowAoVivoModal'
import ClassicPage from './pages/ClassicPage'
import ClassicGruposPage from './pages/ClassicGruposPage'
import PerfilPage from './pages/PerfilPage'
import PlanosPage from './pages/PlanosPage'
import LandingPage from './pages/LandingPage'
import AdminPage from './pages/AdminPage'
import './styles/global.css'

// Context compartilhado entre GrupoLayout e páginas filhas
// Permite que o controller notifique o layout diretamente (sem depender do broadcast Supabase)
export const ShowContext = createContext(null)
export const useShow = () => useContext(ShowContext)

function RotaProtegida({ children }) {
  const { user, loading } = useAuth()
  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:8, background:'var(--brand)' }}>
      <div style={{ width:96, height:96, marginBottom:8 }}>
        <img src="/logo-app.png" alt="CifrasBand" style={{ width:'100%', height:'100%', objectFit:'contain' }} />
      </div>
      <div style={{ fontFamily:'Montserrat,sans-serif', fontSize:28, fontWeight:800, letterSpacing:1 }}>
        <span style={{ color:'#00CFFF' }}>CIFRAS</span><span style={{ color:'#FF8C00' }}>BAND</span>
      </div>
      <div style={{ fontSize:10, color:'rgba(0,207,255,0.5)', letterSpacing:3, fontWeight:500, fontFamily:'Montserrat,sans-serif', marginBottom:8 }}>
        SINCRONIA QUE SE OUVE
      </div>
      <div className="spinner" />
    </div>
  )
  return user ? children : <Navigate to="/auth" replace />
}

// Rota que exige login + plano ativo. Redireciona para /planos se sem assinatura.
function RotaComPlano({ children }) {
  const { user, loading } = useAuth()
  const { temPlano } = usePlano()
  if (loading) return null
  if (!user) return <Navigate to="/auth" replace />
  if (!temPlano) return <Navigate to="/planos?expirado=true" replace />
  return children
}

function RotaPublica({ children }) {
  const { user, loading, isPasswordRecovery } = useAuth()
  if (loading) return null
  // Permite acesso à /auth mesmo logado quando:
  // 1. Evento PASSWORD_RECOVERY disparado pelo Supabase (mais confiável)
  // 2. URL tem ?modo=nova-senha (fallback para quando o evento ainda não disparou)
  // 3. Hash contém type=recovery (fluxo implicit/legado)
  const params = new URLSearchParams(window.location.search)
  const isRecovery = isPasswordRecovery
    || params.get('modo') === 'nova-senha'
    || window.location.hash.includes('type=recovery')
  return (!user || isRecovery) ? children : <Navigate to="/grupos" replace />
}

// Wrapper persistente para todas as páginas de grupo — usa Outlet para que
// o canal de show ao vivo continue ativo durante navegação entre Feed/Setlists/Membros
function GrupoLayout() {
  const { grupoId } = useParams()
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [showAtivo, setShowAtivo] = useState(null)
  const [showAberto, setShowAberto] = useState(false)
  // Estado para notificação de Classic show ao vivo
  const [classicShowAtivo, setClassicShowAtivo] = useState(null) // { pecaId, titulo }
  // Ref para evitar toast duplicado
  const jaToastouInicio = useRef(false)
  // Ref closure-safe para o estado do show (usado dentro de listeners do Supabase)
  const showAtivoRef = useRef(null)
  // Canal Supabase exposto via context — ShowAoVivoModal (controller) usa o MESMO
  // objeto para send/track, eliminando dupla subscrição ao mesmo tópico
  const canalRef = useRef(null)

  // Função exposta para o controller notificar este layout DIRETAMENTE
  // (sem depender do Supabase broadcast que não funciona no mesmo socket)
  // Chamada diretamente pelo controller no mesmo dispositivo
  // (broadcast não é entregue ao socket que o enviou)
  function notificarInicio(payload) {
    jaToastouInicio.current = true   // bloqueia toast duplicado se um heartbeat chegar
    showAtivoRef.current = payload
    setShowAtivo(payload)
    // O controller já sabe que iniciou — sem toast aqui
  }

  function notificarFim() {
    jaToastouInicio.current = false
    showAtivoRef.current = null
    setShowAtivo(null)
    setShowAberto(false)
    toast('⏹ Show encerrado', { icon: '🎸', style: { background: '#071629', color: '#f0f0f0' } })
  }

  useEffect(() => {
    if (!grupoId || !profile?.id) return
    jaToastouInicio.current = false
    showAtivoRef.current = null

    // true após o SUBSCRIBED confirmar — qualquer 'sync' depois disso é real-time
    // (o catch-up de late-joiner chega antes, enquanto ainda é false)
    let pastInitialWindow = false
    let windowTimer = null

    const canal = supabase.channel(`show:${grupoId}`)
    canalRef.current = canal

    canal
      // ── Presence ──
      // IMPORTANTE: o viewer também faz canal.track() → isto é necessário para
      // que o Supabase entregue presence_diffs de OUTROS clientes (controller).
      // Clientes que apenas escutam sem track podem não receber diffs.
      .on('presence', { event: 'sync' }, () => {
        const state = canal.presenceState()
        const ctrl = Object.values(state).flat().find(p => p.tipo === 'controller')

        if (ctrl?.showState) {
          const eraNulo = !showAtivoRef.current
          showAtivoRef.current = ctrl.showState
          setShowAtivo(ctrl.showState)

          if (pastInitialWindow && eraNulo && !jaToastouInicio.current) {
            jaToastouInicio.current = true
            toast(`🔴 Show ao vivo iniciado por ${ctrl.showState.controladorNome || 'Músico'}`, {
              duration: 5000,
              style: { background: '#e07800', color: '#fff', fontWeight: 600 }
            })
          }
        } else if (!ctrl && showAtivoRef.current) {
          // Controller saiu do presence (show encerrado via untrack)
          // O broadcast 'fim' também chega, mas este garante limpeza em qualquer caso
          showAtivoRef.current = null
          jaToastouInicio.current = false
        }
      })
      // ── Broadcast: camada de redundância ──
      .on('broadcast', { event: 'show_update' }, ({ payload }) => {
        if (!payload) return
        if (payload.tipo === 'fim') {
          jaToastouInicio.current = false
          showAtivoRef.current = null
          setShowAtivo(null)
          setShowAberto(false)
          toast('⏹ Show encerrado', { icon: '🎸', style: { background: '#071629', color: '#f0f0f0' } })
        } else {
          showAtivoRef.current = payload
          setShowAtivo(payload)
          if (!jaToastouInicio.current) {
            jaToastouInicio.current = true
            toast(`🔴 Show ao vivo iniciado por ${payload.controladorNome || 'Músico'}`, {
              duration: 5000,
              style: { background: '#e07800', color: '#fff', fontWeight: 600 }
            })
          }
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Viewer participa do presence — necessário para receber diffs do controller
          canal.track({ tipo: 'viewer', userId: profile.id }).catch(() => {})
          // Após 1 s, qualquer sync novo é real-time (show iniciou) → habilita toast
          windowTimer = setTimeout(() => { pastInitialWindow = true }, 1000)
          // Race condition fix: presence.sync inicial pode chegar antes do estado
          // do controller ser propagado. Verifica de novo após 1.5 s para late-joiners.
          setTimeout(() => {
            if (showAtivoRef.current) return // já recebeu via broadcast ou sync
            const state = canal.presenceState()
            const ctrl = Object.values(state).flat().find(p => p.tipo === 'controller')
            if (ctrl?.showState) {
              showAtivoRef.current = ctrl.showState
              setShowAtivo(ctrl.showState)
            }
          }, 1500)
        }
      })

    return () => {
      clearTimeout(windowTimer)
      canal.untrack().catch(() => {})
      canalRef.current = null
      supabase.removeChannel(canal)
    }
  }, [grupoId, profile?.id])

  // Subscrição ao canal Classic do grupo — escuta quando maestro inicia/encerra peça
  useEffect(() => {
    if (!grupoId || !profile?.id) return
    let pronto = false
    let timer = null

    const canalClassic = supabase.channel(`classic:${grupoId}:show`)

    canalClassic
      .on('broadcast', { event: 'iniciar_classico' }, ({ payload }) => {
        if (!pronto || !payload?.pecaId) return
        // Não notifica quem JÁ está na tela do show (evita banner redundante)
        setClassicShowAtivo({ pecaId: payload.pecaId, titulo: payload.titulo || 'Peça iniciada' })
        toast(`🎼 Maestro iniciou: ${payload.titulo || 'Peça'}`, {
          duration: 8000,
          style: { background: '#0a1f3a', color: '#fff', border: '1px solid rgba(0,207,255,0.3)' },
        })
      })
      .on('broadcast', { event: 'encerrar_classico' }, () => {
        setClassicShowAtivo(null)
      })
      .subscribe(status => {
        if (status === 'SUBSCRIBED') {
          canalClassic.track({ tipo: 'viewer', userId: profile.id }).catch(() => {})
          timer = setTimeout(() => { pronto = true }, 1000)
        }
      })

    return () => {
      clearTimeout(timer)
      canalClassic.untrack().catch(() => {})
      supabase.removeChannel(canalClassic)
    }
  }, [grupoId, profile?.id])

  // canalRef exposto para ShowAoVivoModal usar o MESMO canal (sem dupla subscrição)
  const contextValue = { showAtivo, setShowAtivo, showAberto, setShowAberto, notificarInicio, notificarFim, canalRef }

  return (
    <ShowContext.Provider value={contextValue}>
      {/* Outlet renderiza FeedPage / SetlistsPage / MembrosPage */}
      <Outlet />

      {/* Banner flutuante CifrasBand show */}
      {showAtivo && !showAberto && (
        <div className="show-banner" onClick={() => setShowAberto(true)}>
          <span className="ao-vivo-dot" />
          <div style={{ flex:1 }}>
            <div style={{ color:'#fff', fontWeight:700, fontSize:13, fontFamily:'Montserrat, sans-serif' }}>Show ao vivo!</div>
            <div style={{ color:'rgba(255,255,255,0.8)', fontSize:11 }}>{showAtivo.setlistNome} · {showAtivo.posicao + 1}/{showAtivo.total}</div>
          </div>
          <div style={{ background:'rgba(255,255,255,0.2)', padding:'6px 12px', borderRadius:50, color:'#fff', fontSize:12, fontWeight:700, flexShrink:0 }}>Entrar ›</div>
        </div>
      )}

      {/* Banner Classic show ao vivo — aparece quando maestro inicia uma peça */}
      {classicShowAtivo && (
        <div
          className="show-banner"
          style={{ background: 'linear-gradient(135deg,#071629,#0a2540)', borderTop: '2px solid rgba(0,207,255,0.4)', bottom: showAtivo ? 64 : 0 }}
          onClick={() => {
            navigate(`/classic/${grupoId}?autoshow=${classicShowAtivo.pecaId}`)
            setClassicShowAtivo(null)
          }}>
          <span className="ao-vivo-dot" style={{ background: '#00CFFF', boxShadow: '0 0 6px #00CFFF' }} />
          <div style={{ flex:1 }}>
            <div style={{ color:'#00CFFF', fontWeight:700, fontSize:13, fontFamily:'Montserrat, sans-serif' }}>🎼 Classic ao vivo!</div>
            <div style={{ color:'rgba(255,255,255,0.7)', fontSize:11 }}>{classicShowAtivo.titulo}</div>
          </div>
          <div style={{ background:'rgba(0,207,255,0.2)', padding:'6px 12px', borderRadius:50, color:'#00CFFF', fontSize:12, fontWeight:700, flexShrink:0 }}>Entrar ›</div>
        </div>
      )}

      {/* Modal viewer CifrasBand — abre ao clicar no banner */}
      {showAberto && showAtivo && (
        <ShowAoVivoModal
          mode="viewer"
          cifraAtual={showAtivo.cifraAtual}
          tomUsado={showAtivo.tomUsado}
          posicao={showAtivo.posicao}
          total={showAtivo.total}
          controladorNome={showAtivo.controladorNome}
          setlistNome={showAtivo.setlistNome}
          grupoId={grupoId}
          userName={profile?.nome || 'Músico'}
          modoVocal={profile?.modo_vocal || profile?.instrumento === 'Voz'}
          onClose={() => setShowAberto(false)}
        />
      )}
    </ShowContext.Provider>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" toastOptions={{
          style: { background:'#071629', color:'#f0f0f0', border:'1px solid rgba(255,255,255,0.1)', borderRadius:12, fontSize:14 },
          duration: 3000
        }} />
        <Routes>
          <Route path="/" element={<RotaPublica><LandingPage /></RotaPublica>} />
          <Route path="/auth" element={<RotaPublica><AuthPage /></RotaPublica>} />
          <Route path="/grupos" element={<RotaComPlano><GruposPage /></RotaComPlano>} />
          <Route path="/perfil" element={<RotaProtegida><PerfilPage /></RotaProtegida>} />
          <Route path="/planos" element={<RotaProtegida><PlanosPage /></RotaProtegida>} />
          <Route path="/admin" element={<RotaProtegida><AdminPage /></RotaProtegida>} />
          {/* Rotas de grupo: GrupoLayout persiste ao navegar entre Feed/Setlists/Membros */}
          <Route path="/grupo/:grupoId" element={<RotaComPlano><GrupoLayout /></RotaComPlano>}>
            <Route index element={<FeedPage />} />
            <Route path="setlists" element={<SetlistsPage />} />
            <Route path="membros" element={<MembrosPage />} />
            <Route path="classic" element={<ClassicPage />} />
          </Route>
          {/* Módulo Classic — grupos separados */}
          <Route path="/classic" element={<RotaComPlano><ClassicGruposPage /></RotaComPlano>} />
          <Route path="/classic/:grupoId" element={<RotaComPlano><GrupoLayout /></RotaComPlano>}>
            <Route index element={<ClassicPage />} />
            <Route path="membros" element={<MembrosPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/grupos" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}
